// Unsaved-changes support for the form builder:
//  - serializeBuilderState: what save-form stores for one page, as a stable
//    string, so "dirty" is simply current string !== saved string.
//  - a local backup (draft) of unsaved page state in localStorage.

// Field properties that save-form actually stores (see Form_builder_server.js
// save-form). Everything else on a field is editor-only state (the sample
// answer typed on the canvas, the selected option, ...) and never makes the
// page dirty.
const SAVED_FIELD_KEYS = [
    "type", "label", "placeholder", "caption", "default_value", "description",
    "alert_type", "font_size", "required", "alignment", "heading_alignment",
    "min_value", "max_value", "btnalignment", "btnbgColor", "btnlabelColor",
    "field_uid", "show_if", "style", "bubble", "rows", "columns",
    "previewSize", "youtubeUrl", "thankyou_heading", "thankyou_subtext", "hideIcon",
    "prefill_source", "prefill_locked",
];

const SAVED_OPTION_KEYS = ["option_text", "option_uid", "options_style", "image_path"];

function isFile(value) {
    return typeof File !== "undefined" && value instanceof File;
}

// A File cannot be compared or stored; describe it instead.
function describeFile(value) {
    return isFile(value) ? { file: value.name, size: value.size, modified: value.lastModified } : value;
}

function projectOption(opt) {
    if (opt === null || typeof opt !== "object") return opt;
    const out = {};
    SAVED_OPTION_KEYS.forEach(k => { out[k] = describeFile(opt[k]) ?? null; });
    return out;
}

function projectField(field) {
    const out = {};
    SAVED_FIELD_KEYS.forEach(k => {
        const v = field[k];
        out[k] = v === undefined ? null : v;
    });
    out.required = field.required === true || field.required === "Yes";
    out.options = Array.isArray(field.options) ? field.options.map(projectOption) : null;
    out.file = describeFile(field.file) ?? null;
    out.uploads = Array.isArray(field.uploads)
        ? field.uploads.map(u => ({ file_path: u.file_path ?? null, youtube_url: u.youtube_url ?? null }))
        : null;
    return out;
}

// The page's savable state as one string. Order of fields matters.
export function serializeBuilderState({ fields, formTitle, theme }) {
    return JSON.stringify({
        title: formTitle ?? "",
        theme: {
            background: theme.formBgColor ?? null,
            questionsBackground: theme.formColor ?? null,
            primary: theme.formPrimaryColor ?? null,
            questions: theme.formQuestionColor ?? null,
            answers: theme.formAnswersColor ?? null,
            font: theme.selectedFont ?? null,
            backgroundImage: theme.formbgImage ?? null,
        },
        fields: (fields || []).map(projectField),
    });
}

// ---- Local backup --------------------------------------------------------

export const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export function draftKey(formId, pageId) {
    return `dforms_draft_${formId}_${pageId}`;
}

function storage() {
    try {
        return typeof window !== "undefined" ? window.localStorage : null;
    } catch (e) {
        return null;
    }
}

// Copy of the builder state that can go into localStorage. File objects (new,
// unsaved uploads) cannot be stored: they are dropped and counted.
export function toDraftState({ fields, formTitle, theme }) {
    let droppedUploads = 0;
    const cleanFields = (fields || []).map(field => {
        const copy = { ...field };
        if (isFile(copy.file)) {
            copy.file = null;
            droppedUploads++;
        }
        if (Array.isArray(copy.options)) {
            copy.options = copy.options.map(opt => {
                if (opt && typeof opt === "object" && isFile(opt.image_path)) {
                    droppedUploads++;
                    return { ...opt, image_path: null };
                }
                return opt;
            });
        }
        return copy;
    });
    return { state: { fields: cleanFields, formTitle, theme: { ...theme } }, droppedUploads };
}

// Drop embedded images (data: URLs) when the full draft is too big to store.
function withoutDataUrls(state) {
    let dropped = 0;
    const strip = v => {
        if (typeof v === "string" && v.startsWith("data:")) {
            dropped++;
            return null;
        }
        return v;
    };
    const fields = state.fields.map(f => ({
        ...f,
        options: Array.isArray(f.options)
            ? f.options.map(o => (o && typeof o === "object" ? { ...o, image_path: strip(o.image_path) } : o))
            : f.options,
    }));
    const theme = { ...state.theme, formbgImage: strip(state.theme.formbgImage) };
    return { state: { ...state, fields, theme }, dropped };
}

export function writeDraft(formId, pageId, builderState, fieldsVersion, now = Date.now()) {
    const store = storage();
    if (!store) return false;
    const { state, droppedUploads } = toDraftState(builderState);
    const payload = (s, dropped) => JSON.stringify({
        state: s,
        savedAt: now,
        fields_version: fieldsVersion ?? 0,
        droppedUploads: dropped,
    });
    try {
        store.setItem(draftKey(formId, pageId), payload(state, droppedUploads));
        return true;
    } catch (e) {
        // Quota: keep the draft without embedded images rather than nothing.
        try {
            const slim = withoutDataUrls(state);
            store.setItem(draftKey(formId, pageId), payload(slim.state, droppedUploads + slim.dropped));
            return true;
        } catch (e2) {
            return false;
        }
    }
}

export function deleteDraft(formId, pageId) {
    const store = storage();
    if (!store) return;
    try {
        store.removeItem(draftKey(formId, pageId));
    } catch (e) { /* ignore */ }
}

// A usable draft for this page, or null. Unusable drafts are deleted silently:
// older than 7 days, or saved against an older version of the page (someone
// saved since then), or not newer than the page's last save.
export function readDraft(formId, pageId, { fieldsVersion, pageSavedAt, now = Date.now() } = {}) {
    const store = storage();
    if (!store) return null;
    let draft = null;
    try {
        draft = JSON.parse(store.getItem(draftKey(formId, pageId)) || "null");
    } catch (e) {
        draft = null;
    }
    if (!draft) {
        deleteDraft(formId, pageId);
        return null;
    }
    const savedAt = Number(draft.savedAt) || 0;
    const tooOld = now - savedAt > DRAFT_MAX_AGE_MS;
    const staleVersion = (Number(draft.fields_version) || 0) < (Number(fieldsVersion) || 0);
    const notNewer = pageSavedAt && savedAt <= pageSavedAt;
    if (!draft.state || !Array.isArray(draft.state.fields) || tooOld || staleVersion || notNewer) {
        deleteDraft(formId, pageId);
        return null;
    }
    return draft;
}
